import { useState } from "react"
import { useQuery } from "urql"
import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis } from "recharts"
import { Zap, CheckCircle2, XCircle, Mail } from "lucide-react"
import { Skeleton } from "@/components/ui/skeleton"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { DashboardTrendsQuery } from "@/lib/graphql/queries"
import { useRefetchOnFocus } from "@/hooks/use-refetch-on-focus"
import type { ResultOf } from "@/lib/graphql/types"

type Point = ResultOf<typeof DashboardTrendsQuery>["dashboardTrends"]["eventsReceived"][number]

interface SparklineCardProps {
  label: string
  data: Point[]
  color: string
  icon: React.ReactNode
  iconColor: string
  loading: boolean
}

function formatHour(timestamp: string) {
  const d = new Date(timestamp)
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
}

function SparklineCard({ label, data, color, icon, iconColor, loading }: SparklineCardProps) {
  const total = data.reduce((sum, p) => sum + p.value, 0)
  const gradientId = `gradient-${label.replace(/\s+/g, "-")}`

  return (
    <div className="rounded-lg glass-card p-5 animate-fade-in-up">
      <div className="flex items-center gap-3 mb-3">
        <div className={`rounded-lg p-2 ${iconColor}`}>
          {icon}
        </div>
        <div>
          <div className="text-xs text-muted-foreground">{label}</div>
          <div className="text-lg font-bold tabular-nums">
            {loading ? <Skeleton className="h-6 w-12" /> : total.toLocaleString()}
          </div>
        </div>
      </div>
      <div className="h-16">
        {loading ? (
          <Skeleton className="h-full w-full rounded" />
        ) : data.length > 1 ? (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data} margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
              <defs>
                <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={color} stopOpacity={0.3} />
                  <stop offset="100%" stopColor={color} stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="timestamp" hide />
              <Tooltip
                content={({ active, payload }) => {
                  if (!active || !payload?.[0]) return null
                  const p = payload[0].payload as Point
                  return (
                    <div className="rounded-md bg-popover px-3 py-1.5 text-xs shadow-md border">
                      <div className="font-medium">{formatHour(p.timestamp)}</div>
                      <div className="text-muted-foreground">{p.value.toLocaleString()}</div>
                    </div>
                  )
                }}
              />
              <Area
                type="monotone"
                dataKey="value"
                stroke={color}
                strokeWidth={1.5}
                fill={`url(#${gradientId})`}
                dot={false}
                activeDot={{ r: 3, fill: color }}
              />
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-full flex items-center justify-center text-xs text-muted-foreground">
            No data yet
          </div>
        )}
      </div>
    </div>
  )
}

const timeRangeOptions = [
  { value: "6", label: "Last 6 Hours" },
  { value: "12", label: "Last 12 Hours" },
  { value: "24", label: "Last 24 Hours" },
  { value: "48", label: "Last 2 Days" },
  { value: "72", label: "Last 3 Days" },
  { value: "168", label: "Last 7 Days" },
] as const

export function TrendCharts() {
  const [hours, setHours] = useState(24)
  const [result, reexecute] = useQuery({ query: DashboardTrendsQuery, variables: { hours } })
  useRefetchOnFocus(reexecute)

  const trends = result.data?.dashboardTrends
  const loading = !trends && result.fetching

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Trends</h3>
        <Select value={String(hours)} onValueChange={(v) => setHours(Number(v))}>
          <SelectTrigger className="w-40 h-7 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {timeRangeOptions.map((opt) => (
              <SelectItem key={opt.value} value={opt.value} className="text-xs">
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <SparklineCard
          label="Events Received"
          data={trends?.eventsReceived ?? []}
          color="#34d399"
          icon={<Zap className="h-4 w-4 text-emerald-400" />}
          iconColor="bg-emerald-500/15"
          loading={loading}
        />
        <SparklineCard
          label="Delivered"
          data={trends?.deliveriesCompleted ?? []}
          color="#2dd4bf"
          icon={<CheckCircle2 className="h-4 w-4 text-teal-400" />}
          iconColor="bg-teal-500/15"
          loading={loading}
        />
        <SparklineCard
          label="Failed"
          data={trends?.deliveriesFailed ?? []}
          color="#f87171"
          icon={<XCircle className="h-4 w-4 text-red-400" />}
          iconColor="bg-red-500/15"
          loading={loading}
        />
        <SparklineCard
          label="Outbound Messages"
          data={trends?.outboundMessages ?? []}
          color="#818cf8"
          icon={<Mail className="h-4 w-4 text-indigo-400" />}
          iconColor="bg-indigo-500/15"
          loading={loading}
        />
      </div>
    </div>
  )
}
